import { useChatApi } from '@/hooks/useChatApi';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/store/chatStore';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { memo, useCallback, useRef, useState } from 'react';

const MessageFeedback = ({ messageId }: { messageId: string }) => {
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);

  const { setShowNegativeFeedbackMessageId, setShowFeedbackAlert } = useChatStore();

  const { sendFeedback } = useChatApi();

  // Use ref to avoid recreating callbacks when feedback changes
  const feedbackRef = useRef(feedback);
  feedbackRef.current = feedback;

  const handlePositiveFeedback = useCallback(() => {
    if (feedbackRef.current) return;
    setFeedback('positive');
    setShowFeedbackAlert(true);
    sendFeedback({
      messageId,
      content: 'positive',
      type: 'positive',
    });
  }, [messageId, setShowFeedbackAlert, sendFeedback]);

  const handleNegativeFeedback = useCallback(() => {
    if (feedbackRef.current) return;
    setFeedback('negative');
    setShowNegativeFeedbackMessageId(messageId);
  }, [messageId, setShowNegativeFeedbackMessageId]);

  return (
    <div
      className={cn('flex gap-[16px]', {
        'pointer-events-none': feedback,
      })}
    >
      <ThumbsUp
        className={cn(
          'cursor-pointer text-[#A4A7AE] transition-all duration-300 hover:fill-widget-accent-200 hover:text-widget-accent-600',
          {
            'fill-widget-accent-200 text-widget-accent-600': feedback === 'positive',
          }
        )}
        size={16}
        onClick={handlePositiveFeedback}
      />
      <ThumbsDown
        className={cn(
          'cursor-pointer text-[#A4A7AE] transition-all duration-300 hover:fill-widget-accent-200 hover:text-widget-accent-600',
          {
            'fill-widget-accent-200 text-widget-accent-600': feedback === 'negative',
          }
        )}
        size={16}
        onClick={handleNegativeFeedback}
      />
    </div>
  );
};

export default memo(MessageFeedback);
